import { useState } from "react";
import { useForm } from "react-hook-form";
import { Play, AlertCircle, CheckCircle, Users, Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useBookings } from "@/hooks/bookings/useBookings";
import { useBookingStatuses } from "@/hooks/useEventManagement";
import { useTestEventSystem, useEventFlows, useForceRetriggerEvents } from "@/hooks/useEventSystem";
import { supabase } from "@/integrations/supabase/client";

interface TestFlowFormData {
  testMode: 'status_change' | 'event_flow' | 'debug_mode';
  bookingId: string;
  newStatus?: string;
  eventFlowId?: string;
  notes?: string;
  forceRetrigger?: boolean;
}

export function FlowTestingPanel() {
  const [testResult, setTestResult] = useState<any>(null);
  const [isTestingInProgress, setIsTestingInProgress] = useState(false);
  const [recipientPreview, setRecipientPreview] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const { bookings } = useBookings();
  const { data: statuses } = useBookingStatuses();
  const { data: eventFlows } = useEventFlows();
  const testEventSystem = useTestEventSystem();
  const forceRetriggerEvents = useForceRetriggerEvents();

  const form = useForm<TestFlowFormData>({
    defaultValues: {
      testMode: "status_change",
      bookingId: "",
      newStatus: "",
      eventFlowId: "",
      notes: "",
      forceRetrigger: false,
    },
  });

  // Preview recipients when booking and status change
  const previewRecipients = async (bookingId: string, newStatus: string) => {
    if (!bookingId || !newStatus) return;
    
    setPreviewLoading(true);
    try {
      // Find matching event flows for this status transition
      const matchingFlows = eventFlows?.filter(flow => 
        flow.is_active && 
        flow.to_status === newStatus
      ) || [];

      if (matchingFlows.length === 0) {
        setRecipientPreview({ flows: [], recipients: null });
        return;
      }

      // Get recipients for the first matching flow
      const firstFlow = matchingFlows[0];
      const { data, error } = await supabase.rpc('resolve_event_recipients', {
        p_recipient_config: firstFlow.recipient_config,
        p_booking_id: bookingId
      });

      if (error) throw error;

      setRecipientPreview({
        flows: matchingFlows,
        recipients: data,
        selectedFlow: firstFlow
      });
    } catch (error) {
      console.error('[RECIPIENT-PREVIEW] Error:', error);
      setRecipientPreview({ error: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const onSubmit = async (data: TestFlowFormData) => {
    setIsTestingInProgress(true);
    setTestResult(null);

    try {
      let result;
      
      if (data.testMode === 'event_flow') {
        // Test specific event flow directly
        result = await testSpecificEventFlow(data.bookingId, data.eventFlowId!, data.notes);
      } else if (data.testMode === 'debug_mode') {
        // Debug mode - analyze what's missing
        result = await debugEventFlowConfiguration(data.bookingId, data.newStatus);
      } else {
        // Standard status change testing
        if (data.forceRetrigger) {
          const currentBooking = bookings?.find(b => b.id === data.bookingId);
          const currentStatus = currentBooking?.status || 'unknown';
          
          result = await forceRetriggerEvents.mutateAsync({
            bookingId: data.bookingId,
            fromStatus: currentStatus,
            toStatus: data.newStatus!,
            notes: data.notes,
          });
        } else {
          result = await testEventSystem.mutateAsync({
            bookingId: data.bookingId,
            newStatus: data.newStatus!,
            notes: data.notes,
          });
        }
      }
      
      setTestResult({ success: true, data: result });
    } catch (error) {
      setTestResult({ 
        success: false, 
        error: error instanceof Error ? error.message : "Unknown error" 
      });
    } finally {
      setIsTestingInProgress(false);
    }
  };

  const testSpecificEventFlow = async (bookingId: string, eventFlowId: string, notes?: string) => {
    console.log('[FLOW-TEST] Testing specific event flow:', { bookingId, eventFlowId });
    
    // Get the event flow details
    const flow = eventFlows?.find(f => f.id === eventFlowId);
    if (!flow) {
      throw new Error('Event flow not found');
    }

    // Validate flow has to_status (from_status can be NULL for creation events)
    if (!flow.to_status) {
      throw new Error(`Event flow "${flow.name}" is missing to_status.`);
    }

    console.log('[FLOW-TEST] Using transition:', {
      from: flow.from_status,
      to: flow.to_status,
      flowName: flow.name
    });

    // Force trigger using the actual transition statuses
    const { data, error } = await supabase.rpc('force_retrigger_events', {
      p_booking_id: bookingId,
      p_from_status: flow.from_status,
      p_to_status: flow.to_status,
      p_notes: notes || `Testing specific event flow: ${flow.name}`
    });

    if (error) throw error;
    return { 
      ...(data as Record<string, any> || {}), 
      test_mode: 'event_flow', 
      flow_tested: flow,
      actual_transition_used: {
        from_status: flow.from_status,
        to_status: flow.to_status
      }
    };
  };

  const debugEventFlowConfiguration = async (bookingId: string, newStatus?: string) => {
    console.log('[FLOW-TEST] Debug mode analysis:', { bookingId, newStatus });
    
    const booking = bookings?.find(b => b.id === bookingId);
    if (!booking) {
      throw new Error('Booking not found');
    }

    const analysis = {
      booking_status: booking.status,
      target_status: newStatus,
      issues: [] as string[],
      suggestions: [] as string[],
      available_transitions: [] as any[],
      available_flows: [] as any[],
      missing_pieces: [] as string[]
    };

    // Check if target status exists
    if (newStatus) {
      const statusExists = statuses?.find(s => s.status_key === newStatus);
      if (!statusExists) {
        analysis.issues.push(`Target status '${newStatus}' does not exist`);
        analysis.suggestions.push(`Create the '${newStatus}' status in booking statuses`);
      }

      // REMOVED: Check if transition exists in deleted table
      // Open transitions: any status can transition to any other
      console.log('[DEBUG] Open transitions - no transition table check needed');

      // Check if event flows exist for this transition
      const matchingFlows = eventFlows?.filter(flow => 
        flow.from_status === booking.status && 
        flow.to_status === newStatus
      ) || [];

      if (matchingFlows.length === 0) {
        analysis.issues.push(`No event flows configured for '${booking.status}' → '${newStatus}' transition`);
        analysis.suggestions.push(`Create an event flow for this transition to enable email notifications`);
        analysis.missing_pieces.push('event_flow');
      } else {
        analysis.available_flows = matchingFlows;
      }
    }

    // Get all possible flows from current status
    const allFlowsFromCurrent = eventFlows?.filter(flow => 
      flow.from_status === booking.status
    ) || [];

    return {
      analysis,
      available_flows_from_current: allFlowsFromCurrent,
      test_mode: 'debug',
      can_auto_fix: analysis.missing_pieces.length > 0
    };
  };

  const activeStatuses = statuses?.filter(s => s.is_active) || [];
  const recentBookings = bookings?.slice(0, 10) || [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Enhanced Event Testing System</CardTitle>
          <CardDescription>
            Test event flows by status change, specific flow, or debug configuration issues
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Test Mode Selector */}
            <div className="space-y-2">
              <Label htmlFor="testMode">Test Mode*</Label>
              <Select
                value={form.watch("testMode")}
                onValueChange={(value: any) => {
                  form.setValue("testMode", value);
                  // Reset dependent fields
                  form.setValue("newStatus", "");
                  form.setValue("eventFlowId", "");
                  setRecipientPreview(null);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select test mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="status_change">
                    <div className="flex flex-col items-start">
                      <span className="font-medium">Status Change</span>
                      <span className="text-xs text-muted-foreground">Test full booking status transition</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="event_flow">
                    <div className="flex flex-col items-start">
                      <span className="font-medium">Specific Event Flow</span>
                      <span className="text-xs text-muted-foreground">Test a specific event flow directly</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="debug_mode">
                    <div className="flex flex-col items-start">
                      <span className="font-medium">Debug Mode</span>
                      <span className="text-xs text-muted-foreground">Analyze configuration and find issues</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bookingId">Test Booking*</Label>
                <Select
                  value={form.watch("bookingId")}
                  onValueChange={(value) => {
                    form.setValue("bookingId", value);
                    previewRecipients(value, form.watch("newStatus"));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a booking to test" />
                  </SelectTrigger>
                  <SelectContent>
                    {recentBookings.map((booking) => (
                      <SelectItem key={booking.id} value={booking.id}>
                        <div className="flex flex-col items-start">
                          <span className="font-mono text-sm">
                            {booking.id.slice(0, 8)}...
                          </span>
                          <span className="text-xs text-muted-foreground">
                            Status: {booking.status}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Showing recent bookings for testing
                </p>
              </div>

              {/* Conditional fields based on test mode */}
              {form.watch("testMode") === "status_change" || form.watch("testMode") === "debug_mode" ? (
                <div className="space-y-2">
                  <Label htmlFor="newStatus">
                    {form.watch("testMode") === "debug_mode" ? "Target Status (optional)" : "New Status*"}
                  </Label>
                  <Select
                    value={form.watch("newStatus") || ""}
                    onValueChange={(value) => {
                      form.setValue("newStatus", value);
                      if (form.watch("testMode") === "status_change") {
                        previewRecipients(form.watch("bookingId"), value);
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select target status" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeStatuses.map((status) => (
                        <SelectItem key={status.status_key} value={status.status_key}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full border"
                              style={{ backgroundColor: status.color }}
                            />
                            {status.display_name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="eventFlowId">Event Flow*</Label>
                  <Select
                    value={form.watch("eventFlowId") || ""}
                    onValueChange={(value) => {
                      form.setValue("eventFlowId", value);
                      // Show flow details
                      const flow = eventFlows?.find(f => f.id === value);
                      if (flow && form.watch("bookingId")) {
                        previewRecipients(form.watch("bookingId"), flow.to_status || "");
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select event flow to test" />
                    </SelectTrigger>
                    <SelectContent>
                      {eventFlows?.map((flow) => (
                        <SelectItem key={flow.id} value={flow.id}>
                          <div className="flex flex-col items-start">
                            <span className="font-medium">{flow.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {flow.from_status || 'any'} → {flow.to_status} • {flow.template_category}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Test Notes</Label>
              <Textarea
                id="notes"
                placeholder="Optional notes for this test (will be included in logs)"
                {...form.register("notes")}
              />
            </div>

            {form.watch("testMode") === "status_change" && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="forceRetrigger"
                  {...form.register("forceRetrigger")}
                  className="rounded border-input"
                />
                <Label htmlFor="forceRetrigger" className="text-sm">
                  Force retrigger events (bypass status change check)
                </Label>
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full"
              disabled={
                isTestingInProgress || 
                !form.watch("bookingId") || 
                (form.watch("testMode") === "status_change" && !form.watch("newStatus")) ||
                (form.watch("testMode") === "event_flow" && !form.watch("eventFlowId"))
              }
            >
              <Play className="h-4 w-4 mr-2" />
              {isTestingInProgress ? 
                "Testing..." : 
                form.watch("testMode") === "debug_mode" ? "Analyze Configuration" :
                form.watch("testMode") === "event_flow" ? "Test Event Flow" :
                form.watch("forceRetrigger") ? "Force Retrigger Events" : "Run Status Test"
              }
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Recipient Preview */}
      {recipientPreview && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Event Flow & Recipient Preview
            </CardTitle>
          </CardHeader>
          <CardContent>
            {previewLoading ? (
              <div className="text-center py-4 text-muted-foreground">
                Loading recipient preview...
              </div>
            ) : recipientPreview.error ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Error loading recipients: {recipientPreview.error}
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-4">
                {/* Matching Event Flows */}
                <div>
                  <h4 className="font-medium mb-2">Matching Event Flows ({recipientPreview.flows?.length || 0})</h4>
                  {recipientPreview.flows?.length > 0 ? (
                    <div className="space-y-2">
                      {recipientPreview.flows.map((flow: any) => (
                        <div key={flow.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                          <div>
                            <span className="font-medium">{flow.name}</span>
                            <div className="text-xs text-muted-foreground">
                              {flow.template_category} • {flow.channels.join(', ')}
                            </div>
                          </div>
                          <Badge variant="outline">
                            Priority {flow.priority}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded">
                      ⚠️ No active event flows found for this status transition
                    </div>
                  )}
                </div>

                {/* Recipients */}
                {recipientPreview.recipients && (
                  <div>
                    <h4 className="font-medium mb-2">
                      Recipients ({recipientPreview.recipients.summary?.total_count || 0})
                    </h4>
                    {recipientPreview.recipients.recipients?.length > 0 ? (
                      <div className="space-y-2">
                        {recipientPreview.recipients.recipients.map((recipient: any, index: number) => (
                          <div key={index} className="flex items-center gap-3 p-2 bg-blue-50 dark:bg-blue-950/20 rounded">
                            <Badge variant="secondary">{recipient.type}</Badge>
                            <div className="flex items-center gap-2 text-sm">
                              {recipient.has_email && (
                                <div className="flex items-center gap-1">
                                  <Mail className="w-3 h-3 text-green-600" />
                                  <span>{recipient.email}</span>
                                </div>
                              )}
                              {recipient.has_phone && (
                                <div className="flex items-center gap-1">
                                  <Phone className="w-3 h-3 text-blue-600" />
                                  <span>{recipient.phone}</span>
                                </div>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground ml-auto">
                              {recipient.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground p-3 bg-red-50 dark:bg-red-950/20 rounded">
                        ❌ No recipients found - check recipient configuration
                      </div>
                    )}
                    
                    {/* Summary Stats */}
                    {recipientPreview.recipients.summary && (
                      <div className="mt-3 grid grid-cols-4 gap-4 text-center text-xs">
                        <div>
                          <div className="font-medium">Total</div>
                          <div className="text-muted-foreground">{recipientPreview.recipients.summary.total_count}</div>
                        </div>
                        <div>
                          <div className="font-medium">Email</div>
                          <div className="text-green-600">{recipientPreview.recipients.summary.email_count}</div>
                        </div>
                        <div>
                          <div className="font-medium">Phone</div>
                          <div className="text-blue-600">{recipientPreview.recipients.summary.phone_count}</div>
                        </div>
                        <div>
                          <div className="font-medium">Missing</div>
                          <div className="text-red-600">{recipientPreview.recipients.summary.missing_email_count}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {testResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {testResult.success ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-500" />
              )}
              Test Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            {testResult.success ? (
              <div className="space-y-4">
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    Event system test completed successfully!
                  </AlertDescription>
                </Alert>
                
                {testResult.data && (
                  <div className="space-y-3">
                    {/* Debug Mode Results */}
                    {testResult.data.test_mode === 'debug' && (
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium mb-2">Configuration Analysis:</h4>
                          <div className="space-y-3">
                            {testResult.data.analysis.issues.length > 0 && (
                              <div className="bg-red-50 dark:bg-red-950/20 p-3 rounded">
                                <h5 className="font-medium text-red-700 dark:text-red-300 mb-2">Issues Found:</h5>
                                <ul className="text-sm text-red-600 dark:text-red-400 space-y-1">
                                  {testResult.data.analysis.issues.map((issue: string, i: number) => (
                                    <li key={i}>• {issue}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {testResult.data.analysis.suggestions.length > 0 && (
                              <div className="bg-blue-50 dark:bg-blue-950/20 p-3 rounded">
                                <h5 className="font-medium text-blue-700 dark:text-blue-300 mb-2">Suggestions:</h5>
                                <ul className="text-sm text-blue-600 dark:text-blue-400 space-y-1">
                                  {testResult.data.analysis.suggestions.map((suggestion: string, i: number) => (
                                    <li key={i}>• {suggestion}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {testResult.data.available_flows_from_current.length > 0 && (
                              <div className="bg-green-50 dark:bg-green-950/20 p-3 rounded">
                                <h5 className="font-medium text-green-700 dark:text-green-300 mb-2">
                                  Available Event Flows from '{testResult.data.analysis.booking_status}':
                                </h5>
                                <div className="space-y-2">
                                  {testResult.data.available_flows_from_current.map((flow: any) => (
                                    <div key={flow.id} className="text-sm text-green-600 dark:text-green-400 flex justify-between">
                                      <span>{flow.name}</span>
                                      <span className="text-xs">→ {flow.transition?.to_status}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Standard Test Results */}
                    <div>
                      <h4 className="font-medium mb-2">
                        {testResult.data.test_mode === 'debug' ? 'Raw Analysis Data:' : 'Test Summary:'}
                      </h4>
                      <div className="bg-muted rounded p-3 text-sm">
                        <pre className="whitespace-pre-wrap">
                          {JSON.stringify(testResult.data, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Test failed: {testResult.error}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Testing Notes</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <div className="space-y-3">
            <div>
              <strong>Status Change Mode:</strong>
              <ul className="ml-4 mt-1 space-y-1">
                <li>• Tests full booking status transitions and event orchestration</li>
                <li>• Force retrigger bypasses status change checks</li>
                <li>• Validates transitions exist and flows are configured</li>
              </ul>
            </div>
            <div>
              <strong>Event Flow Mode:</strong>
              <ul className="ml-4 mt-1 space-y-1">
                <li>• Tests specific event flows directly without status changes</li>
                <li>• Useful for testing flow configurations in isolation</li>
                <li>• Bypasses transition validation</li>
              </ul>
            </div>
            <div>
              <strong>Debug Mode:</strong>
              <ul className="ml-4 mt-1 space-y-1">
                <li>• Analyzes configuration and identifies missing pieces</li>
                <li>• Shows available flows and transitions</li>
                <li>• Provides suggestions for fixing issues</li>
                <li>• Does not send actual emails</li>
              </ul>
            </div>
          </div>
          <p className="pt-2 border-t"><strong>Note:</strong> Emails will be sent in Status Change and Event Flow modes if flows are active. Check Event Monitoring to see triggered events.</p>
        </CardContent>
      </Card>
    </div>
  );
}